import React, { Children, useRef } from 'react';
import { Box, useBreakpointValue } from '@chakra-ui/react';
import { chakra, shouldForwardProp } from '@chakra-ui/react';
import { motion, isValidMotionProp, useScroll, useTransform } from 'framer-motion';
import useMotionAllowed from './useMotionAllowed';

const MotionBox = chakra(motion.div, {
  shouldForwardProp: (prop) => isValidMotionProp(prop) || shouldForwardProp(prop),
});

/**
 * A page section rendered as a rounded sheet that slides up over the one
 * before it.
 *
 * The effect is two parts working together. Structurally the sheet has
 * rounded top corners, an opaque background and a small negative top margin,
 * so it genuinely overlaps and occludes the section above; an upward shadow
 * sells the separation. Then, as it scrolls in, it eases out of a very slight
 * backward tilt and up to full size, so it reads as coming to rest against
 * the page.
 *
 * The tilt is deliberately shallow. The reference implementation uses 24
 * degrees, which is striking on an agency site and far too theatrical for a
 * surgical center, so this uses 10 and drops to 6 on phones where a large
 * rotation is most likely to feel like the page is falling over.
 *
 * The sheet needs an opaque background to overlap convincingly. Pass `bg` to
 * match the section's own surface.
 */
export const Sheet = ({
  children,
  bg = 'white',
  overlap = 32,
  radius = { base: '24px', md: '40px' },
  ...props
}) => {
  const allowed = useMotionAllowed();
  const ref = useRef(null);
  const narrow = useBreakpointValue({ base: true, md: false }, { ssr: false });

  const tilt = narrow ? 6 : 10;
  const from = narrow ? 0.97 : 0.94;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 92%', 'end 70%'],
  });

  const rotateX = useTransform(scrollYProgress, [0, 1], [tilt, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [from, 1]);

  return (
    <Box
      ref={ref}
      position="relative"
      zIndex={1}
      mt={`-${overlap}px`}
      // The tilt rotates the sheet around its top edge, which swings the
      // bottom edge toward the viewer, and under perspective anything nearer
      // renders larger: a full-width sheet ends up wider than the viewport and
      // the whole page scrolls sideways. Clip the horizontal overflow here.
      // `clip`, not `hidden`, because hidden would make this a scroll
      // container and break position: sticky for anything inside the sheet,
      // such as StackCards.
      sx={{ perspective: '1600px', overflowX: 'clip' }}
    >
      <MotionBox
        bg={bg}
        borderTopRadius={radius}
        style={allowed ? { rotateX, scale } : undefined}
        sx={{ transformOrigin: '50% 0%' }}
        boxShadow="0 -18px 46px -30px rgba(15, 45, 75, 0.35)"
        {...props}
      >
        {children}
      </MotionBox>
    </Box>
  );
};

/**
 * A run of steps that stack as you scroll.
 *
 * Each card sticks a little lower than the one before it, so earlier steps
 * stay visible as thin ledges while the current one holds the screen. The
 * reader keeps their place in the sequence instead of losing the steps they
 * have already passed, which suits an ordered process better than a plain
 * list.
 *
 * Sticky positioning is pure CSS and keeps working when motion is off, so
 * this degrades to a readable stacked list rather than disappearing. That is
 * why it is not gated on the motion preference.
 *
 * `top` clears the fixed header. Adjust if the header height changes.
 */
export const StackCards = ({ children, top = 96, step = 14, gap = 6, ...props }) => (
  <Box display="grid" gap={gap} {...props}>
    {Children.map(children, (child, i) => (
      <Box
        position="sticky"
        top={`${top + i * step}px`}
        sx={{ transformOrigin: '50% 0%' }}
      >
        {child}
      </Box>
    ))}
  </Box>
);

/**
 * The hero recedes gently as the page moves past it.
 *
 * The background drifts down and grows very slightly while the content above
 * it fades, so the hero feels like it is being left behind rather than
 * scrolled off. Amplitudes are about a third of the reference: 8% of travel
 * and 6% of growth, where a marketing hero would use 16% and 14%.
 *
 * Wrap the hero's background layer. Content stays in normal flow above it.
 */
export const HeroRecede = ({ children, distance = 600, ...props }) => {
  const allowed = useMotionAllowed();

  // A hero sits at the top of the document, so page scroll is the natural
  // driver. Measuring against the element itself would require it to have
  // scrolled past before the value moved, which is the wrong window entirely
  // for something meant to react as the reader first pushes down.
  const { scrollY } = useScroll();

  const y = useTransform(scrollY, [0, distance], [0, 48]);
  const scale = useTransform(scrollY, [0, distance], [1, 1.06]);

  return (
    <MotionBox
      style={allowed ? { y, scale } : undefined}
      sx={{ willChange: 'transform' }}
      {...props}
    >
      {children}
    </MotionBox>
  );
};

export default Sheet;
